/**
 * Which side of the portal the sidebar should render.
 *
 * The TopBar pill reads the view's portalMode directly. The sidebar used to
 * apply one extra rule on top: a superuser/staff account on a non-dual-mode
 * company got the SUPPLIER menu whenever PlatformSettings.main_menu_type was
 * '2' — regardless of what they were looking at. Two things made that wrong
 * for every Helix admin viewing-as a customer:
 *
 *  - supportsDualMode is computed from the admin's OWN company (the Helix
 *    platform tenant), never from the company they have selected, so it is
 *    always false for them; and
 *  - main_menu_type defaults to '2', so any tenant that never changed the
 *    setting triggered the override.
 *
 * Result: pill says Customer, context says the customer company, sidebar
 * shows My Onboarding / Sales Orders / RFQs / Customers.
 *
 * Rule now: when a Helix-internal user has a specific company selected, that
 * company's type decides the side. The platform default only applies when
 * nothing is selected (group level), which is the case it was written for.
 * Every non-Helix path is unchanged.
 */
export type NavPortalMode = 'customer' | 'supplier';

export interface ResolveNavPortalModeInput {
  /** The view's portal mode (what the pill shows). */
  portalMode: string;
  /** True for Helix platform accounts (platform HELIX / company_type helix). */
  isVCInternal: boolean;
  /** Django is_superuser || is_staff. */
  isAdminUser: boolean;
  /** Whether the user's own company is dual-mode (ViewContext). */
  supportsDualMode: boolean;
  /** PlatformSettings.main_menu_type ('1' customer, '2' supplier, '3' network). */
  mainMenuType: string;
  /** True while platform settings are still loading — the default must not fire early. */
  menuSettingsLoading: boolean;
  /** The company currently selected in the view, when one is. */
  viewedCompany?: { company_type?: string | null; portal_mode?: string | null } | null;
}

/**
 * Resolve the sidebar's portal side.
 *
 * @returns 'customer' or 'supplier'
 */
export function resolveNavPortalMode(input: ResolveNavPortalModeInput): NavPortalMode {
  const { portalMode, isVCInternal, isAdminUser, supportsDualMode, mainMenuType, menuSettingsLoading, viewedCompany } = input;

  // A Helix-internal user looking at a specific company sees that company's
  // side of the portal, in step with the pill and the context selector. For
  // a dual-mode company the pill's switcher decides, exactly as it does for
  // that company's own users.
  if (isVCInternal && viewedCompany) {
    if (viewedCompany.company_type === 'supplier') return 'supplier';
    if (viewedCompany.portal_mode === 'both') return portalMode === 'supplier' ? 'supplier' : 'customer';
    return 'customer';
  }

  // Platform default for admins with no company selected and no dual mode.
  if (isAdminUser && !supportsDualMode && !menuSettingsLoading && mainMenuType === '2') {
    return 'supplier';
  }

  return portalMode === 'supplier' ? 'supplier' : 'customer';
}
